import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Loader2, Save, MailSearch, Users, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const NUMS = [
  ['rate_per_minute', 'Max emails per minute'],
  ['max_attempts', 'Max delivery attempts'],
  ['soft_bounce_threshold', 'Soft bounces before suppression'],
  ['soft_bounce_window_days', 'Soft bounce window (days)'],
  ['duplicate_window_hours', 'Duplicate window (hours)'],
  ['invitations_daily_cap', 'Invitations (bulk uploads): emails per day'],
  ['reminders_daily_cap', 'Reminders (digest + follow-ups): emails per day'],
];

export function SettingsTab() {
  const { api } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [imapPw, setImapPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [probe, setProbe] = useState('');
  const [probeResult, setProbeResult] = useState(null);
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    api.get('/api/admin/email/settings').then(({ data }) => setCfg(data));
    api.get('/api/admin/email/audit-users').then(({ data }) => setAudit(data));
  }, [api]);

  useEffect(() => {
    if (!audit?.running) return undefined;
    const t = setInterval(() => api.get('/api/admin/email/audit-users').then(({ data }) => setAudit(data)), 3000);
    return () => clearInterval(t);
  }, [audit?.running, api]);

  if (!cfg) return <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" />;
  const setImap = (k, v) => setCfg({ ...cfg, imap: { ...cfg.imap, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg, imap: { ...cfg.imap, ...(imapPw ? { password: imapPw } : {}) } };
      NUMS.forEach(([k]) => { payload[k] = Number(payload[k]); });
      payload.imap.port = Number(payload.imap.port);
      const { data } = await api.put('/api/admin/email/settings', payload);
      setCfg(data); setImapPw('');
      toast.success('Email delivery settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const pollNow = async () => {
    const { data } = await api.post('/api/admin/email/bounces/poll-now');
    data.ok ? toast.success(`Checked ${data.messages} message(s), ${data.bounces} bounce(s)`) : toast.error(data.error);
  };

  const runProbe = async () => {
    const { data } = await api.post('/api/admin/email/validate', { email: probe });
    setProbeResult(data);
  };

  const runAudit = async () => {
    const { data } = await api.post('/api/admin/email/audit-users');
    setAudit({ ...audit, ...data });
    toast.info('Scanning existing user emails…');
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="email-settings-tab">
      <Card className="bg-[#041A3B] border-white/10">
        <CardHeader><CardTitle className="text-white text-base">Delivery rules</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {NUMS.map(([k, label]) => (
            <div key={k} className="flex items-center justify-between gap-3">
              <Label className="text-[#A0B4CC]">{label}</Label>
              <Input type="number" value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
                className="w-28 bg-[#000B18] border-white/10 text-white" data-testid={`setting-${k}`} />
            </div>
          ))}
          <Toggle label="Require verified email for marketing emails" checked={cfg.require_verified_for_marketing}
            onChange={(v) => setCfg({ ...cfg, require_verified_for_marketing: v })} testId="setting-require-verified" />
          <Toggle label="Block sign-ups with undeliverable emails" checked={cfg.block_invalid_signups}
            onChange={(v) => setCfg({ ...cfg, block_invalid_signups: v })} testId="setting-block-invalid-signups" />
        </CardContent>
      </Card>

      <Card className="bg-[#041A3B] border-white/10">
        <CardHeader><CardTitle className="text-white text-base">Bounce mailbox (IMAP)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-[#5C7599]">The inbox that receives Mailer-Daemon replies (usually your From address). Polled every 10 minutes.</p>
          <Toggle label="Enable bounce polling" checked={cfg.imap.enabled} onChange={(v) => setImap('enabled', v)} testId="setting-imap-enabled" />
          <div className="grid grid-cols-3 gap-2">
            <Input className="col-span-2 bg-[#000B18] border-white/10 text-white" placeholder="imap.gmail.com" value={cfg.imap.host} onChange={(e) => setImap('host', e.target.value)} data-testid="setting-imap-host" />
            <Input className="bg-[#000B18] border-white/10 text-white" type="number" value={cfg.imap.port} onChange={(e) => setImap('port', e.target.value)} data-testid="setting-imap-port" />
          </div>
          <Input className="bg-[#000B18] border-white/10 text-white" placeholder="Username" value={cfg.imap.username} onChange={(e) => setImap('username', e.target.value)} data-testid="setting-imap-username" />
          <Input className="bg-[#000B18] border-white/10 text-white" type="password" placeholder={cfg.imap.password_configured ? '•••••••• (saved)' : 'App password'} value={imapPw} onChange={(e) => setImapPw(e.target.value)} data-testid="setting-imap-password" />
          <Input className="bg-[#000B18] border-white/10 text-white" placeholder="Folder (INBOX)" value={cfg.imap.folder} onChange={(e) => setImap('folder', e.target.value)} data-testid="setting-imap-folder" />
          <Button variant="ghost" className="text-[#00E5FF]" onClick={pollNow} data-testid="imap-poll-now-btn"><MailSearch className="w-4 h-4 mr-2" />Check mailbox now</Button>
        </CardContent>
      </Card>

      <Card className="bg-[#041A3B] border-white/10">
        <CardHeader><CardTitle className="text-white text-base">Check an address</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={probe} onChange={(e) => setProbe(e.target.value)} placeholder="someone@domain.com" className="bg-[#000B18] border-white/10 text-white" data-testid="validate-email-input" />
            <Button onClick={runProbe} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#5CF0FF]" data-testid="validate-email-btn">Check</Button>
          </div>
          {probeResult && (
            <div className="text-sm" data-testid="validate-email-result">
              <span className={probeResult.valid ? 'text-[#00E676]' : 'text-[#FF5252]'}>{probeResult.valid ? 'Deliverable' : `Rejected: ${probeResult.reason}`}</span>
              {probeResult.suggestion && <span className="text-[#A0B4CC]"> · did you mean {probeResult.suggestion}?</span>}
              {probeResult.suppressed && <span className="text-[#FF9100]"> · suppressed ({probeResult.suppressed.reason})</span>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[#041A3B] border-white/10">
        <CardHeader><CardTitle className="text-white text-base">Scan existing users</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-[#5C7599]">Runs a DNS/MX check on every user email and suppresses addresses that can't receive mail.</p>
          <Button onClick={runAudit} disabled={audit?.running} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#5CF0FF]" data-testid="audit-users-btn">
            {audit?.running ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}{audit?.running ? 'Scanning…' : 'Run scan'}
          </Button>
          {audit?.result && (
            <p className="text-sm text-[#A0B4CC] flex items-center gap-2" data-testid="audit-users-result">
              <CheckCircle2 className="w-4 h-4 text-[#00E676]" />Scanned {audit.result.scanned}, flagged {audit.result.flagged}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="lg:col-span-2">
        <Button onClick={save} disabled={saving} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#5CF0FF]" data-testid="email-settings-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save settings
        </Button>
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label className="text-[#A0B4CC]">{label}</Label>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}
